import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import type {
    TemplateCreate as CreateTemplateMutation,
    TemplateCreateVariables as CreateTemplateMutationVariables,
    TemplateDuplicate as DuplicateTemplateMutation,
    TemplateDuplicateVariables as DuplicateTemplateMutationVariables,
    DeleteBroadcast as DeleteTemplateMutation,
    DeleteBroadcastVariables as DeleteTemplateMutationVariables,
    RestoreBroadcast as RestoreTemplateMutation,
    RestoreBroadcastVariables as RestoreTemplateMutationVariables,
    UpdateBroadcast as UpdateTemplateMutation,
    UpdateBroadcastVariables as UpdateTemplateMutationVariables,
    GetTemplates as GetTemplatesQuery,
    EditionUpdateInput,
    GetTemplatesVariables as GetTemplatesQueryVariables,
} from 'nutshell-graphql-types';

// These do!
import GET_TEMPLATES from '../editions/graphql/queries/get-templates.graphql';
import DELETE_BROADCAST from '../graphql-wrappers/broadcast/graphql/mutations/delete-broadcast.graphql';
import RESTORE_BROADCAST from '../graphql-wrappers/broadcast/graphql/mutations/restore-broadcast.graphql';
import UPDATE_BROADCAST from '../graphql-wrappers/broadcast/graphql/mutations/update-broadcast.graphql';

import CREATE_TEMPLATE from './graphql/mutations/template-create.graphql';
import DUPLICATE_TEMPLATE from './graphql/mutations/template-duplicate.graphql';

type Props = {
    children: (arg1: {
        onCreate: (name: string, queryVariables?: GetTemplatesQueryVariables) => Promise<any>;
        onDuplicate: (
            templateId: string,
            name?: string | null | undefined,
            queryVariables?: GetTemplatesQueryVariables
        ) => Promise<any>;
        onDelete: (templateId: string, queryVariables?: GetTemplatesQueryVariables) => Promise<any>;
        onRestore: (
            templateId: string,
            queryVariables?: GetTemplatesQueryVariables
        ) => Promise<any>;
        onUpdate: (input: EditionUpdateInput, templateId: string) => Promise<any>;
    }) => React.ReactElement;
};

export function TemplateMutations(props: Props) {
    const [createTemplate] = useMutation<CreateTemplateMutation, CreateTemplateMutationVariables>(
        CREATE_TEMPLATE
    );

    const [duplicateTemplate] = useMutation<
        DuplicateTemplateMutation,
        DuplicateTemplateMutationVariables
    >(DUPLICATE_TEMPLATE);

    const [deleteTemplate] = useMutation<DeleteTemplateMutation, DeleteTemplateMutationVariables>(
        DELETE_BROADCAST
    );

    const [restoreTemplate] = useMutation<
        RestoreTemplateMutation,
        RestoreTemplateMutationVariables
    >(RESTORE_BROADCAST);

    const [updateTemplate] = useMutation<UpdateTemplateMutation, UpdateTemplateMutationVariables>(
        UPDATE_BROADCAST
    );

    return props.children({
        onCreate: (name: string, queryVariables?: GetTemplatesQueryVariables) => {
            return createTemplate({
                variables: {
                    input: {
                        name: name,
                    },
                },
                update: (proxy, mutationResults) => {
                    const newTemplate =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.templateCreate &&
                        mutationResults.data.templateCreate.template;

                    if (!newTemplate) return;

                    templateCacheUpdate(proxy, newTemplate, false, queryVariables);
                },
            }).then(
                (mutationResults) =>
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.templateCreate &&
                    mutationResults.data.templateCreate.template
            );
        },
        onDuplicate: (
            templateId: string,
            name?: string | null,
            queryVariables?: GetTemplatesQueryVariables
        ) => {
            return duplicateTemplate({
                variables: {edition: {editionId: templateId, name: name}},
                update: (proxy, mutationResults) => {
                    const newTemplate =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.editionDuplicateToTemplate &&
                        mutationResults.data.editionDuplicateToTemplate.template;

                    if (!newTemplate) return;

                    templateCacheUpdate(proxy, newTemplate, false, queryVariables);
                },
            }).then(
                (mutationResults) =>
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.editionDuplicateToTemplate &&
                    mutationResults.data.editionDuplicateToTemplate.template
            );
        },
        onDelete: (templateId: string, queryVariables?: GetTemplatesQueryVariables) => {
            return deleteTemplate({
                variables: {id: templateId},
                update: (proxy, mutationResults) => {
                    const deletedTemplate =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.campaignDelete &&
                        mutationResults.data.campaignDelete.campaign;

                    if (!deletedTemplate) return;

                    templateCacheUpdate(proxy, deletedTemplate, true, queryVariables);
                },
            });
        },
        onRestore: (templateId: string, queryVariables?: GetTemplatesQueryVariables) => {
            return restoreTemplate({
                variables: {id: templateId},
                update: (proxy, mutationResults) => {
                    const restoredTemplate =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.campaignRestore &&
                        mutationResults.data.campaignRestore.campaign;

                    if (!restoredTemplate) return;

                    templateCacheUpdate(proxy, restoredTemplate, false, queryVariables);
                },
            });
        },
        onUpdate: (saveData: EditionUpdateInput, templateId: string) => {
            return updateTemplate({
                variables: {
                    edition: saveData,
                },
                update: (proxy, mutationResults) => {
                    // Read the data from our cache for this query.
                    const cacheResult: GetTemplatesQuery | null | undefined = proxy.readQuery({
                        query: GET_TEMPLATES,
                        variables: {templateId: templateId},
                    });

                    if (!cacheResult || !cacheResult.templates) {
                        return;
                    }

                    const updatedEdition =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.editionUpdate &&
                        mutationResults.data.editionUpdate.edition;
                    if (!updatedEdition) return;

                    proxy.writeQuery({
                        query: GET_TEMPLATES,
                        variables: {templateId: templateId},
                        data: {
                            ...cacheResult.templates,
                            edition: updatedEdition,
                            name: updatedEdition.campaign.name,
                        },
                    });
                },
            });
        },
    });
}

/**
 * This will handle updating our local cache when we archive or restore a
 * template. It correctly handles updating both the archived and unarchived
 * caches.
 *
 * @param {DataProxy} proxy The mutation update proxy
 * @param {Template} template The template that was mutated
 * @param {boolean} isDeleting True if we are deleting the given template,
 * false if we are restoring it.
 */
const templateCacheUpdate = (
    // @ts-expect-error
    proxy,
    // @ts-expect-error
    template,
    isDeleting: boolean,
    queryVariables?: GetTemplatesQueryVariables
) => {
    // Passing a value for `isArchived` here isn't really needed because we
    // overwrite it when writing to the cache below, but hey I made it required
    const variables = queryVariables || {};

    try {
        // Update our archived cache
        // This isn't the best variable name, but its the cache we're removing
        // an template from.
        const cacheResultRemoving: GetTemplatesQuery | null | undefined = proxy.readQuery({
            query: GET_TEMPLATES,
            variables: {...variables, archived: !isDeleting},
        });

        if (cacheResultRemoving && cacheResultRemoving.templates) {
            proxy.writeQuery({
                query: GET_TEMPLATES,
                variables: {...variables, archived: !isDeleting},
                data: {
                    templates: {
                        ...cacheResultRemoving.templates,
                        edges: cacheResultRemoving.templates.edges.filter((edge) => {
                            return edge.node.id !== template.id;
                        }),
                    },
                },
            });
        }
    } catch {
        // If the above query doesn't exist in the cache, Apollo will throw an
        // error (why it can't just return null is beyond me). If that happens,
        // we need to "catch" the error. But we don't want to really do anything
        // with it. The just means we haven't performed this query yet. So, an
        // empty catch block
    }

    // Update our unarchived cache
    // Again, not the best name. We're adding an template to this cache
    try {
        const cacheResultAdding: GetTemplatesQuery | null | undefined = proxy.readQuery({
            query: GET_TEMPLATES,
            variables: {...variables, archived: isDeleting},
        });

        if (!cacheResultAdding || !cacheResultAdding.templates) {
            return;
        }
        proxy.writeQuery({
            query: GET_TEMPLATES,
            variables: {...variables, archived: isDeleting},
            data: {
                templates: {
                    ...cacheResultAdding.templates,
                    edges: [
                        {
                            cursor: template.id,
                            node: template,
                            __typename: 'AudiencesEdge',
                        },
                    ].concat(cacheResultAdding.templates.edges),
                },
            },
        });
    } catch {
        // If the above query doesn't exist in the cache, Apollo will throw an
        // error (why it can't just return null is beyond me). If that happens,
        // we need to "catch" the error. But we don't want to really do anything
        // with it. The just means we haven't performed this query yet. So, an
        // empty catch block
    }
};
